import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Box,
    Chip,
    Paper,
    Button,
    Avatar,
    Divider,
    useTheme,
    TextField,
    IconButton,
    Tooltip,
    CircularProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import {
    Share as ShareIcon,
    GetApp as GetAppIcon,
    Delete as DeleteIcon,
    Launch as OpenIcon,
    Edit as EditIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    CalendarToday as CalendarIcon,
    People as PeopleIcon,
    AttachMoney as MoneyIcon,
    Gavel as GavelIcon,
    Autorenew as AutorenewIcon,
} from '@mui/icons-material';
import { getStatusKey, getStatusStyle, formatDate } from '../../../utils/utils';
import { fetchKeyInfo } from '../../../redux/actions/contractAction';

// Key info exposes total_value (+ currency); append the currency unless it's already in the string
const formatKeyInfoValue = (keyInfo) => {
  const value = keyInfo?.total_value
  if (!value) return null
  const currency = keyInfo.currency
  return currency && !String(value).toUpperCase().includes(String(currency).toUpperCase())
    ? `${value} ${currency}`
    : value
}

// Key info row component
const InfoRow = ({ icon: Icon, label, value, theme }) => (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Icon sx={{ fontSize: 15, color: theme.palette.text.secondary, mt: 0.3, flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
                variant="caption"
                sx={{
                    color: theme.palette.text.secondary,
                    fontSize: '0.65rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    fontWeight: 600,
                    lineHeight: 1.2,
                }}
            >
                {label}
            </Typography>
            <Typography
                sx={{
                    fontSize: '0.8rem',
                    color: value ? theme.palette.text.primary : theme.palette.text.disabled,
                    wordBreak: 'break-word',
                    lineHeight: 1.3,
                }}
            >
                {value || '\u2014'}
            </Typography>
        </Box>
    </Box>
);

// Create a safe HTML renderer component
const HtmlContent = ({ content, theme }) => {
    // Sanitize and clean the HTML content but preserve styles
    const cleanContent = content
        .replace(/<script[^>]*>.*?<\/script>/gis, '')
        .replace(/<meta[^>]*>/gis, '')
        .replace(/<link[^>]*>/gis, '')
        .replace(/<head[^>]*>.*?<\/head>/gis, '')
        .replace(/<body[^>]*>|<\/body>/gis, '')
        .replace(/<html[^>]*>|<\/html>/gis, '');

    // Extract inline styles from <style> tags to inject them scoped
    const styleMatches = content.match(/<style[^>]*>([\s\S]*?)<\/style>/gi) || [];
    const extractedStyles = styleMatches
        .map(s => s.replace(/<\/?style[^>]*>/gi, ''))
        .join('\n');

    // Remove style tags from content (we'll inject them separately scoped)
    const contentWithoutStyles = cleanContent.replace(/<style[^>]*>.*?<\/style>/gis, '');

    return (
        <div className="contract-preview-content">
            {extractedStyles && (
                <style dangerouslySetInnerHTML={{ __html: `.contract-preview-content { ${extractedStyles} }` }} />
            )}
            <div
                dangerouslySetInnerHTML={{ __html: contentWithoutStyles }}
                style={{
                    fontFamily: "'Inter', 'Segoe UI', sans-serif",
                    fontSize: '0.85rem',
                    lineHeight: 1.7,
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                    maxWidth: '100%',
                    color: theme.palette.text.primary,
                }}
            />
        </div>
    );
};

const ContractDetailDialog = ({ open, onOpen, onClose, contract, onDownload, onShare, onDelete, onUpdateContract }) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const dispatch = useDispatch();
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [editedTitle, setEditedTitle] = useState('');

    const keyInfo = useSelector((state) => state.contract?.keyInfo);
    const loadingKeyInfo = useSelector((state) => state.contract?.loadingKeyInfo);

    const goldAccent = '#c9a96e';

    // Fetch key info when contract changes
    useEffect(() => {
        if (open && contract?.id) {
            dispatch(fetchKeyInfo(contract.id));
        }
    }, [open, contract?.id, dispatch]);

    if (!contract) return null;

    // Determine if content is HTML or plain text
    const isHtml = contract.content &&
        (contract.content.includes('<') &&
            contract.content.includes('</') &&
            contract.content.includes('>'));

    const handleEditTitle = () => {
        setEditedTitle(contract.title);
        setIsEditingTitle(true);
    };

    const handleSaveTitle = async () => {
        if (editedTitle.trim() !== contract.title) {
            try {
                await onUpdateContract(contract.id, { title: editedTitle.trim() });
            } catch (error) {
                console.error('Error updating contract title:', error);
            }
        }
        setIsEditingTitle(false);
    };

    const handleCancelEdit = () => {
        setIsEditingTitle(false);
        setEditedTitle(contract.title);
    };

    const partiesText = keyInfo?.parties?.length
        ? keyInfo.parties.map((p) => p.party_name).join(', ')
        : null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '12px',
                    border: `1px solid ${theme.palette.divider}`,
                    boxShadow: theme.palette.mode === 'dark' ? '0 4px 20px rgba(0,0,0,0.5)' : '0 4px 20px rgba(0, 0, 0, 0.1)',
                    height: '85vh',
                    display: 'flex',
                    flexDirection: 'column',
                }
            }}
        >
            {/* Header: Title + Status */}
            <DialogTitle sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {isEditingTitle ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, mr: 1 }}>
                            <TextField
                                value={editedTitle}
                                onChange={(e) => setEditedTitle(e.target.value)}
                                variant="outlined"
                                size="small"
                                fullWidth
                                autoFocus
                                sx={{ mr: 1 }}
                            />
                            <Tooltip title={t("save_changes")}>
                                <IconButton onClick={handleSaveTitle} color="primary" size="small">
                                    <SaveIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={t("cancel")}>
                                <IconButton onClick={handleCancelEdit} size="small">
                                    <CancelIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="h5" sx={{ fontWeight: 600 }}>
                                {contract.title}
                            </Typography>
                            <Tooltip title={t("modify")}>
                                <IconButton onClick={handleEditTitle} size="small" sx={{ ml: 1 }}>
                                    <EditIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}
                    <Chip
                        label={t(getStatusKey(contract.status).toLowerCase()) || contract.status}
                        sx={{
                            ...getStatusStyle(contract.status, theme),
                            fontWeight: 600,
                            ml: 1
                        }}
                        size="small"
                    />
                </Box>

                {/* Meta line: type + dates + collaborators */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1, flexWrap: 'wrap' }}>
                    {contract.type && (
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.75rem' }}>
                            {t(`contractCreationType.${contract.type}`, contract.type)}
                        </Typography>
                    )}
                    <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontSize: '0.72rem' }}>
                        {t("created")}: {formatDate(contract.created_at, i18n.language)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontSize: '0.72rem' }}>
                        {t("last_modified")}: {formatDate(contract.updated_at, i18n.language)}
                    </Typography>
                    {contract.collaborators && contract.collaborators.length > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {contract.collaborators.slice(0, 4).map((collaborator, idx) => (
                                <Tooltip key={idx} title={`${collaborator.first_name} ${collaborator.last_name} (${t(collaborator.role)})`}>
                                    <Avatar
                                        sx={{
                                            width: 22,
                                            height: 22,
                                            fontSize: '0.6rem',
                                            fontWeight: 700,
                                            bgcolor: [
                                                theme.palette.primary.main,
                                                theme.palette.success.main,
                                                theme.palette.warning.main,
                                                theme.palette.secondary.main,
                                            ][idx % 4],
                                        }}
                                    >
                                        {collaborator.first_name[0]}{collaborator.last_name[0]}
                                    </Avatar>
                                </Tooltip>
                            ))}
                            {contract.collaborators.length > 4 && (
                                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.7rem', ml: 0.5 }}>
                                    +{contract.collaborators.length - 4}
                                </Typography>
                            )}
                        </Box>
                    )}
                </Box>
            </DialogTitle>

            <DialogContent
                dividers
                sx={{
                    px: 3,
                    py: 2,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                }}
            >
                {/* Key Information Section */}
                <Box sx={{ mb: 2, flexShrink: 0 }}>
                    <Typography
                        variant="subtitle2"
                        sx={{
                            fontWeight: 700,
                            fontSize: '0.72rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            color: theme.palette.text.secondary,
                            mb: 1.5,
                        }}
                    >
                        {t('repository.preview.keyInfo', 'Key Information')}
                    </Typography>

                    {loadingKeyInfo ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
                            <CircularProgress size={20} sx={{ color: goldAccent }} />
                        </Box>
                    ) : keyInfo ? (
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                            gap: 1.5,
                        }}>
                            <InfoRow
                                icon={PeopleIcon}
                                label={t('repository.table.parties', 'Parties')}
                                value={partiesText}
                                theme={theme}
                            />
                            <InfoRow
                                icon={CalendarIcon}
                                label={t('repository.preview.effectiveDate', 'Effective Date')}
                                value={keyInfo.effective_date ? formatDate(keyInfo.effective_date, i18n.language) : null}
                                theme={theme}
                            />
                            <InfoRow
                                icon={CalendarIcon}
                                label={t('repository.table.expiration', 'Expiration')}
                                value={keyInfo.expiration_date ? formatDate(keyInfo.expiration_date, i18n.language) : null}
                                theme={theme}
                            />
                            <InfoRow
                                icon={MoneyIcon}
                                label={t('repository.table.value', 'Value')}
                                value={formatKeyInfoValue(keyInfo)}
                                theme={theme}
                            />
                            <InfoRow
                                icon={GavelIcon}
                                label={t('repository.table.governingLaw', 'Governing Law')}
                                value={keyInfo.governing_law}
                                theme={theme}
                            />
                            <InfoRow
                                icon={AutorenewIcon}
                                label={t('repository.preview.autoRenewal', 'Auto-renewal')}
                                value={
                                    keyInfo.auto_renewal !== undefined && keyInfo.auto_renewal !== null
                                        ? keyInfo.auto_renewal
                                            ? t('yes', 'Yes')
                                            : t('no', 'No')
                                        : null
                                }
                                theme={theme}
                            />
                        </Box>
                    ) : (
                        <Typography
                            variant="body2"
                            sx={{ color: theme.palette.text.disabled, fontStyle: 'italic', fontSize: '0.8rem' }}
                        >
                            {t('repository.preview.noKeyInfo', 'No key information extracted yet.')}
                        </Typography>
                    )}
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Content Preview */}
                <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {t("content_preview")}
                    </Typography>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 0,
                            bgcolor: alpha(theme.palette.background.default, 0.7),
                            borderRadius: 2,
                            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                            flex: 1,
                            minHeight: 0,
                            overflow: 'hidden',
                            position: 'relative',
                        }}
                    >
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                px: 4,
                                py: 3,
                                overflowY: 'auto',
                                // Proper heading and content styles for preview
                                '& h1': {
                                    fontSize: '1.6rem',
                                    fontWeight: 700,
                                    margin: '24px 0 12px 0',
                                    lineHeight: 1.3,
                                    color: theme.palette.text.primary,
                                },
                                '& h2': {
                                    fontSize: '1.3rem',
                                    fontWeight: 600,
                                    margin: '20px 0 10px 0',
                                    lineHeight: 1.3,
                                    color: theme.palette.text.primary,
                                },
                                '& h3': {
                                    fontSize: '1.1rem',
                                    fontWeight: 600,
                                    margin: '16px 0 8px 0',
                                    lineHeight: 1.4,
                                    color: theme.palette.text.primary,
                                },
                                '& h4, & h5, & h6': {
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                    margin: '14px 0 6px 0',
                                    lineHeight: 1.4,
                                    color: theme.palette.text.primary,
                                },
                                '& p': {
                                    margin: '8px 0',
                                    lineHeight: 1.7,
                                },
                                '& img': { maxWidth: '100%', height: 'auto' },
                                '& table': {
                                    borderCollapse: 'collapse',
                                    width: '100%',
                                    margin: '16px 0',
                                    '& td, & th': {
                                        border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                                        padding: '8px 12px',
                                        textAlign: 'left',
                                        fontSize: '0.82rem',
                                    },
                                    '& th': {
                                        fontWeight: 600,
                                        backgroundColor: alpha(theme.palette.divider, 0.08),
                                    },
                                },
                                '& ul, & ol': {
                                    paddingLeft: '24px',
                                    margin: '8px 0',
                                    '& li': {
                                        marginBottom: '4px',
                                        lineHeight: 1.6,
                                    },
                                },
                                '& strong, & b': {
                                    fontWeight: 700,
                                },
                                '& em, & i': {
                                    fontStyle: 'italic',
                                },
                                '& u': {
                                    textDecoration: 'underline',
                                },
                                '& blockquote': {
                                    borderLeft: `3px solid ${goldAccent}`,
                                    margin: '12px 0',
                                    paddingLeft: '16px',
                                    color: theme.palette.text.secondary,
                                },
                                '& a': {
                                    color: theme.palette.primary.main,
                                    textDecoration: 'underline',
                                },
                                '& hr': {
                                    border: 'none',
                                    borderTop: `1px solid ${theme.palette.divider}`,
                                    margin: '16px 0',
                                },
                            }}
                        >
                            {contract.content ? (
                                isHtml ? (
                                    <HtmlContent content={contract.content} theme={theme} />
                                ) : (
                                    <Typography
                                        variant="body2"
                                        component="pre"
                                        sx={{
                                            whiteSpace: 'pre-wrap',
                                            fontFamily: "'Inter', 'Segoe UI', sans-serif",
                                            m: 0,
                                            fontSize: '0.85rem',
                                            lineHeight: 1.7,
                                            wordWrap: 'break-word',
                                            overflowWrap: 'break-word',
                                            maxWidth: '100%',
                                        }}
                                    >
                                        {contract.content}
                                    </Typography>
                                )
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    {t("no_content_available")}
                                </Typography>
                            )}
                        </Box>
                    </Paper>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button
                    onClick={onDelete}
                    color="error"
                    startIcon={<DeleteIcon />}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                    {t("delete")}
                </Button>
                <Box sx={{ flexGrow: 1 }} />
                <Button
                    onClick={onShare}
                    variant="outlined"
                    startIcon={<ShareIcon />}
                    sx={{
                        mr: 1.5,
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: 1.5
                    }}
                >
                    {t("share")}
                </Button>
                <Button
                    onClick={onDownload}
                    variant="outlined"
                    startIcon={<GetAppIcon />}
                    sx={{
                        mr: 1.5,
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: 1.5
                    }}
                >
                    {t("download")}
                </Button>
                <Button
                    onClick={onOpen}
                    variant="contained"
                    endIcon={<OpenIcon />}
                    sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: 1.5,
                        px: 3
                    }}
                >
                    {t("open_contract")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ContractDetailDialog;
